import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { SlackIcon } from "./slackIcon";
import { formatSlackTs, SlackMessageText } from "./slackRenderUtils";
import { parseSlackSearchResultsPayload, type SlackSearchResultsPayload } from "./slackSearchResultsSchema";

interface SlackSearchResultsRendererProps {
  block: StreamingBlock;
}

// Permalinks arrive from tool output and are untrusted; only https URLs are
// safe to put in an href.
const safeSlackPermalink = (url: string | undefined): string | undefined =>
  url?.startsWith("https://") ? url : undefined;

export const SlackSearchResultsRenderer = (props: SlackSearchResultsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<SlackSearchResultsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseSlackSearchResultsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("slack_search_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().matches.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <SlackIcon class="size-4 shrink-0" />
                <span class="truncate text-sm font-medium text-primary-text">{rt.t("slack_search_card.title")}</span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().totalResults === 1
                    ? rt.t("slack_search_card.results_one")
                    : rt.t("slack_search_card.results", { count: String(data().totalResults) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().matches}>
                  {(match) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="flex min-w-0 items-baseline gap-1.5">
                          <span class="truncate text-sm font-medium text-primary-text">{match.author}</span>
                          <span class="shrink-0 text-xs text-muted-text">#{match.channelName}</span>
                        </span>
                        <span class="shrink-0 text-xs text-muted-text">{formatSlackTs(match.ts)}</span>
                      </div>
                      <p class="mt-1 line-clamp-3 text-sm whitespace-pre-wrap text-primary-text">
                        <SlackMessageText text={match.text} mentions={data().mentions} />
                      </p>
                      <Show when={safeSlackPermalink(match.permalink)}>
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="mt-1 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                          >
                            {rt.t("slack_search_card.open_link")}
                            <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
